function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function buildDisplayName(profile) {
  const firstName = pickFirst(profile?.firstName, profile?.first_name);
  const lastName = pickFirst(profile?.lastName, profile?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(profile?.name, profile?.full_name, profile?.username) || '-';
}

function buildCountry(profile) {
  return (
    pickFirst(
      profile?.country,
      profile?.countryName,
      profile?.country_name,
      profile?.countryCode,
      profile?.country_code,
    ) || '-'
  );
}

function buildAssignedToName(profile) {
  const assigned = profile?.assigned || {};
  const firstName = pickFirst(assigned?.firstName, assigned?.first_name);
  const lastName = pickFirst(assigned?.lastName, assigned?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || pickFirst(assigned?.name, assigned?.full_name, assigned?.username, assigned?.emailMasked, assigned?.email_masked) || '-';
}

function normalizeBoolean(value) {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value || '').trim().toLowerCase();
  if (['true', '1', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  return false;
}

function formatDateTime(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  return date.toLocaleString();
}

export function mapProfilesResponseToDto(payload = []) {
  const source = Array.isArray(payload) ? payload : [];

  return source.map((profile, index) => {
    const id = String(pickFirst(profile?.uid, profile?.id) || '').trim();

    return {
      id,
      key: id || `profile-${index}`,
      name: buildDisplayName(profile),
      email: pickFirst(profile?.emailMasked, profile?.email_masked) || '-',
      emailMasked: pickFirst(profile?.emailMasked, profile?.email_masked) || '',
      status: pickFirst(profile?.status) || '-',
      invalid: normalizeBoolean(profile?.invalid),
      country: buildCountry(profile),
      assignedToId: String(pickFirst(profile?.assigned?.uid, profile?.assigned?.id) || '').trim(),
      assignedToName: buildAssignedToName(profile),
      createdAt: formatDateTime(
        pickFirst(profile?.createdAt, profile?.created_at, profile?.createTime, profile?.created),
      ),
      source: profile,
    };
  });
}

export function buildProfilesRequestDto(filters, sort, pagination = {}) {
  const filterBy = [];

  const pushFilter = (field, value) => {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return;
    }

    filterBy.push({ field, filter: normalized });
  };

  pushFilter('firstName', filters?.firstName);
  pushFilter('lastName', filters?.lastName);
  pushFilter('email', filters?.email);
  pushFilter('status', filters?.status);

  const dto = {
    isAdmin: false,
    limit: Number.isInteger(pagination?.limit) && pagination.limit > 0 ? pagination.limit : 20,
    offset: Number.isInteger(pagination?.offset) && pagination.offset > 0 ? pagination.offset : 0,
  };

  if (filterBy.length > 0) {
    dto.filterBy = filterBy;
  }

  if (Array.isArray(filters?.countriesOnly) && filters.countriesOnly.length > 0) {
    dto.countriesOnly = filters.countriesOnly
      .map((item) => String(item || '').trim().toUpperCase())
      .filter(Boolean);
  }

  if (filters?.assignedOnly) {
    dto.assignedOnly = true;
  }

  if (filters?.showInvalidated === true) {
    dto.showInvalidated = true;
  }

  if (sort?.field) {
    dto.sortBy = sort.field;
  }

  if (sort?.order) {
    dto.sortOrder = sort.order;
  }

  return dto;
}
